export const all = {
  'en': 'English',
  'es': 'Español',
  'fr': 'Français',
  'ja': '日本語',
  'ko': '한국어',
  'ru': 'Русский',
  'vi': 'Tiếng Việt',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
}

/**
 * Locales offered to users in the language switcher, in display order.
 * Adding a locale is a two-step change: ship `src/locales/<tag>/` and add the
 * tag here — every picker and the initial-locale resolution pick it up.
 */
export const enabledLocales = ['en', 'ja', 'ko', 'zh-Hant'] as const

/**
 * Endonym labels for the enabled locales, keyed by locale tag.
 * Derived from `all` so the two never drift apart.
 */
export const enabled: Record<string, string> = Object.fromEntries(
  enabledLocales.map(locale => [locale, all[locale]]),
)

/** Where an explicit user choice is persisted. */
export const LANGUAGE_STORAGE_KEY = 'settings/language'

export function isEnabledLocale(locale: string): boolean {
  return (enabledLocales as readonly string[]).includes(locale)
}

export const localeRemap: Record<string, string> = {
  'zh-CN': 'zh-Hans',
  'zh-TW': 'zh-Hant',
  'zh-HK': 'zh-Hant',
  'zh-Hant': 'zh-Hant',
  'zh-Hans': 'zh-Hans',
  'en': 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'en-AU': 'en',
  'es': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  'es-AR': 'es',
  'fr': 'fr',
  'fr-FR': 'fr',
  'ja': 'ja',
  'ja-JP': 'ja',
  'ko': 'ko',
  'ko-KR': 'ko',
  'ru': 'ru',
  'ru-RU': 'ru',
  'vi': 'vi',
  'vi-VN': 'vi',
}

export function resolveSupportedLocale(
  locale: string | null | undefined,
  supportedLocales: readonly string[],
  fallbackLocale = 'en',
): string {
  const normalizedLocale = localeRemap[locale ?? fallbackLocale] ?? locale ?? fallbackLocale

  return supportedLocales.includes(normalizedLocale)
    ? normalizedLocale
    : fallbackLocale
}

/**
 * Pick the locale the UI should boot with: an explicit user choice first
 * (localStorage), then the browser's preferred languages, then `fallbackLocale`.
 *
 * Only locales that are both enabled and actually bundled (`availableLocales`)
 * are considered, so a half-translated bundle is never selected.
 */
export function resolveInitialLocale(
  availableLocales: readonly string[],
  fallbackLocale = 'en',
): string {
  const offered = availableLocales.filter(isEnabledLocale)

  const persisted = globalThis.localStorage?.getItem(LANGUAGE_STORAGE_KEY)
  if (persisted)
    return resolveSupportedLocale(persisted, offered, fallbackLocale)

  if (typeof globalThis.navigator !== 'undefined') {
    const preferred = globalThis.navigator.languages ?? [globalThis.navigator.language]
    for (const candidate of preferred) {
      const normalized = localeRemap[candidate] ?? candidate
      // Try `zh-TW`, then the bare `zh`-style base tag.
      for (const tag of [normalized, normalized.split('-')[0]]) {
        if (offered.includes(tag))
          return tag
      }
    }
  }

  return fallbackLocale
}
